import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect } from 'react';
import type { FC, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import ReactDOM from 'react-dom';

import { MENU_Z_INDEX } from './z-index';

export interface MenuItem {
    id: string;
    name: string;
    color?: string;
    icon?: ReactNode;
    onClick: () => void;
}

export interface MenuProps {
    items: MenuItem[];
    icon?: ReactNode;
    onMenuStateChanged?: (closed: boolean) => void;
}

export const Menu: FC<MenuProps> = ({ items, icon, onMenuStateChanged }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = (e: ReactMouseEvent) => {
        e.stopPropagation();
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        onMenuStateChanged?.(!isOpen);
    }, [onMenuStateChanged, isOpen]);

    const getPosition = () => {
        const menuRect = ref.current?.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const bottom = menuRect?.bottom ?? 0;
        const height = 200;
        const width = 144;
        const heightOverflow = bottom + height - windowHeight;

        let left = menuRect?.left ?? 0;
        let top = menuRect?.bottom ?? 0;

        if (bottom + height > windowHeight) {
            top = top - heightOverflow;
        }

        if (left + width > windowWidth) {
            left = left - width;
        }
        return { top: top, left: left };
    };

    return (
        <div className="relative inline-block" ref={ref}>
            <button type="button" onClick={toggleMenu}>
                {icon ? icon : <EllipsisVerticalIcon className="h-11 w-7 text-white text-left" />}
            </button>
            {isOpen &&
                ReactDOM.createPortal(
                    <div
                        className="absolute w-40 bg-gray-100 shadow-xl rounded-md transition-all duration-100 ease-in-out max-h-[200px] overflow-y-scroll opacity-100 visible"
                        style={{ zIndex: MENU_Z_INDEX, ...getPosition() }}
                        ref={menuRef}
                    >
                        {items.map((item) => (
                            <button
                                type="button"
                                key={item.id}
                                className={`flex flex-row gap-1.5 w-full px-3 py-2 text-${item.color ? item.color : 'white'} text-gray-700 hover:bg-gray-80 rounded-md text-xs text-left items-center`}
                                onClick={(e: ReactMouseEvent) => {
                                    e.stopPropagation();
                                    toggleMenu(e);
                                    item.onClick();
                                }}
                            >
                                {item.icon ? item.icon : ''}
                                {item.name}
                            </button>
                        ))}
                    </div>,
                    document.body,
                )}
        </div>
    );
};
